import type { FlagsType } from '@iheartjane/feature-flags';
import type { QueryClient } from '@tanstack/react-query';
import type { RouteObject } from 'react-router';

import { Layout } from '../components';
import { specialLoader } from '../data-access';
import { BrandSpecial } from '../pages/BrandSpecial/BrandSpecial';
import { FeaturedCategory } from '../pages/FeaturedCategory/FeaturedCategory';
import { Home } from '../pages/Home';
import { LinkBank } from '../pages/LinkBank/LinkBank';
import { Login } from '../pages/Login/Login';
import { Logout } from '../pages/Logout/Logout';
import { Menu } from '../pages/Menu/Menu';
import { Search } from '../pages/Search/Search';
import { SearchResults } from '../pages/SearchResults/SearchResults';
import { Special } from '../pages/Special/Special';
import { SwitchStores } from '../pages/SwitchStores/SwitchStores';
import { MenuProvider } from '../providers/lib/MenuProvider';
import { commsBannerRoute } from './commsBannerRoute';
import { filterRoutes } from './filterRoutes';
import { multiWeightPromptRoute } from './multiWeightPromptRoute';
import { productDetailRoute } from './productDetailRoute';
import type { ProductDetailRouteId, WeightModalRouteId } from './types';

type CommonChildrenProps = {
  pdpRouteId?: ProductDetailRouteId;
  queryClient: QueryClient;
  weightRouteId?: WeightModalRouteId;
};

const commonMenuChildren = ({
  queryClient,
  pdpRouteId,
  weightRouteId,
}: CommonChildrenProps): RouteObject[] => [
  productDetailRoute(pdpRouteId),
  multiWeightPromptRoute(queryClient, weightRouteId),
  filterRoutes,
  menuLoginRoute,
  menuLogoutRoute,
  linkBankRoute,
  {
    children: [
      {
        element: <Search />,
        index: true,
      },
      productDetailRoute(),
    ],
    path: `search`,
  },
];

const searchResultsRoute = (queryClient: QueryClient): RouteObject => ({
  children: [
    {
      children: commonMenuChildren({ queryClient, weightRouteId: 'search' }),
      element: <SearchResults />,
      path: 'search-results',
    },
  ],
  element: (
    <Layout
      isFullScreen
      justifyContent="flex-start"
      backgroundColor="#FFFFFF"
    />
  ),
});

const specialRoute = (
  queryClient: QueryClient,
  flags: FlagsType
): RouteObject => ({
  children: [
    {
      children: commonMenuChildren({
        queryClient,
        pdpRouteId: 'specialPdp',
        weightRouteId: 'specialWeightModal',
      }),
      element: <Special />,
      loader: specialLoader(queryClient, flags),
      path: 'special/:specialId',
      id: 'special',
    },
  ],
  element: (
    <Layout
      alignItems="flex-start"
      justifyContent="flex-start"
      isFullScreen
      backgroundColor="#FFFFFF"
    />
  ),
});

const brandSpecialRoute = (queryClient: QueryClient): RouteObject => ({
  children: [
    {
      children: commonMenuChildren({
        queryClient,
        pdpRouteId: 'brandSpecialPdp',
        weightRouteId: 'brandSpecialWeightModal',
      }),
      element: <BrandSpecial />,
      path: 'jane-gold/:brandSpecialId',
      id: 'brandSpecial',
    },
  ],
  element: (
    <Layout
      alignItems="flex-start"
      justifyContent="flex-start"
      isFullScreen
      backgroundColor="#FFFFFF"
    />
  ),
});

const featuredRoute = (queryClient: QueryClient): RouteObject => ({
  children: [
    {
      children: [
        ...commonMenuChildren({
          queryClient,
          pdpRouteId: 'featuredPdp',
          weightRouteId: 'featuredWeightModal',
        }),
        storeSwitcherRoute,
      ],
      element: <FeaturedCategory />,
      path: 'featured/:featuredCategory',
    },
  ],
  element: (
    <Layout
      isFullScreen
      justifyContent="flex-start"
      backgroundColor="#FFFFFF"
    />
  ),
});

const rowRoute = (queryClient: QueryClient): RouteObject => ({
  children: [
    {
      children: [
        ...commonMenuChildren({
          queryClient,
          pdpRouteId: 'rowPdp',
          weightRouteId: 'rowWeightModal',
        }),
        storeSwitcherRoute,
      ],
      element: <FeaturedCategory />,
      path: 'rows/:rowId/:slug',
    },
  ],
  element: (
    <Layout
      isFullScreen
      justifyContent="flex-start"
      backgroundColor="#FFFFFF"
    />
  ),
});

const storeSwitcherRoute: RouteObject = {
  element: <SwitchStores />,
  path: 'switch-stores',
};

const menuLoginRoute: RouteObject = {
  element: <Login />,
  path: `login`,
};

const menuLogoutRoute: RouteObject = {
  element: <Logout />,
  path: `logout`,
};

const linkBankRoute: RouteObject = {
  element: <LinkBank />,
  path: 'link-bank',
};

export const menuRoutes = (
  queryClient: QueryClient,
  flags: FlagsType
): RouteObject => ({
  children: [
    {
      element: <Home />,
      index: true,
    },
    searchResultsRoute(queryClient),
    {
      children: [
        ...commonMenuChildren({ queryClient }),
        storeSwitcherRoute,
        commsBannerRoute,
      ],
      element: <Menu />,
      path: ':category',
    },
    brandSpecialRoute(queryClient),
    specialRoute(queryClient, flags),
    featuredRoute(queryClient),
    rowRoute(queryClient),
  ],
  element: (
    <MenuProvider>
      <Layout />
    </MenuProvider>
  ),
  id: 'menu',
  path: 'menu',
});
